import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import SectionOverviewTab   from "./tabs/SectionOverviewTab";
import SectionRosterTab     from "./tabs/SectionRosterTab";
import SectionTeachersTab   from "./tabs/SectionTeachersTab";
import SectionBooksTab      from "./tabs/SectionBooksTab";
import SectionFacilitiesTab from "./tabs/SectionFacilitiesTab";
import s from "./SectionProfileModal.module.css";

const ALL_TABS = [
  { key: "overview",    label: "Overview",    icon: "🏫" },
  { key: "roster",      label: "Students",    icon: "👥" },
  { key: "teachers",    label: "Teachers",    icon: "👨‍🏫" },
  { key: "books",       label: "Books",       icon: "📖" },
  { key: "facilities",  label: "Facilities",  icon: "🔧" },
];

/**
 * Comprehensive section profile modal.
 *
 * Props:
 *   open         boolean
 *   section      Section row (must have id, name, gradeLevelId, schoolYearId, homeroomId, adviserStaffId)
 *   gradeLevels  Array<GradeLevel>
 *   schoolYears  Array<SchoolYear>
 *   semesters    Array<Semester>
 *   onClose      fn()
 *   onViewStudent fn(student) — optional, passed to Roster tab so clicking a student opens StudentProfileModal
 */
export default function SectionProfileModal({
  open,
  section,
  gradeLevels = [],
  schoolYears = [],
  semesters   = [],
  onClose,
  onViewStudent,
  zIndex = 1095,
}) {
  const [activeTab,  setActiveTab]  = useState("overview");
  const [loadedTabs, setLoadedTabs] = useState(() => new Set());

  /* Reset when a new section opens */
  useEffect(() => {
    if (!open || !section?.id) return;
    setActiveTab("overview");
    setLoadedTabs(new Set(["overview"]));
  }, [open, section?.id]);

  /* ESC + body scroll lock */
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === "Escape") onClose?.(); }
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  function switchTab(key) {
    setActiveTab(key);
    setLoadedTabs(prev => {
      if (prev.has(key)) return prev;
      return new Set([...prev, key]);
    });
  }

  if (!open || !section) return null;

  /* Header derived data */
  const gradeLevelById = Object.fromEntries(gradeLevels.map(g => [g.id, g]));
  const syById         = Object.fromEntries(schoolYears.map(y => [y.id, y]));
  const gradeLevel     = section.gradeLevelId ? gradeLevelById[section.gradeLevelId] : null;
  const sy             = section.schoolYearId ? syById[section.schoolYearId]         : null;

  function renderTab(key) {
    const common = { section, gradeLevels, schoolYears, semesters };
    switch (key) {
      case "overview":   return <SectionOverviewTab   {...common} />;
      case "roster":     return <SectionRosterTab     section={section} onViewStudent={onViewStudent} />;
      case "teachers":   return <SectionTeachersTab   section={section} semesters={semesters} />;
      case "books":      return <SectionBooksTab      section={section} />;
      case "facilities": return <SectionFacilitiesTab section={section} />;
      default:           return null;
    }
  }

  return createPortal(
    <div
      className={s.backdrop}
      style={{ zIndex }}
      role="presentation">

      <div
        className={s.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="spm-sect-title">

        {/* ── Header ──────────────────────────────────────────────── */}
        <div className={s.header}>
          <div className={s.headerShimmer} aria-hidden="true" />

          <div className={s.headerIcon} aria-hidden="true">🏫</div>

          <div className={s.headerInfo}>
            <div className={s.eyebrow}>Section Record</div>
            <h2 id="spm-sect-title" className={s.headerName}>{section.name}</h2>
            <div className={s.headerMeta}>
              {gradeLevel && (
                <span className={s.metaPill}>{gradeLevel.name}</span>
              )}
              {sy && (
                <span className={s.metaPill}>SY {sy.label}</span>
              )}
            </div>
          </div>

          <button
            type="button"
            className={s.closeBtn}
            onClick={onClose}
            aria-label="Close section profile">
            ✕
          </button>
        </div>

        {/* ── Tab bar ─────────────────────────────────────────────── */}
        <div className={s.tabBar} role="tablist" aria-label="Section profile sections">
          {ALL_TABS.map(tab => (
            <button
              key={tab.key}
              role="tab"
              aria-selected={activeTab === tab.key}
              aria-controls={`spm-sect-panel-${tab.key}`}
              id={`spm-sect-tab-${tab.key}`}
              className={`${s.tab} ${activeTab === tab.key ? s.tabActive : ""}`}
              onClick={() => switchTab(tab.key)}>
              <span className={s.tabIcon} aria-hidden="true">{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </div>

        {/* ── Tab panels ──────────────────────────────────────────── */}
        <div className={s.body}>
          {ALL_TABS.map(tab => (
            <div
              key={tab.key}
              id={`spm-sect-panel-${tab.key}`}
              role="tabpanel"
              aria-labelledby={`spm-sect-tab-${tab.key}`}
              className={activeTab === tab.key ? s.tabPanelActive : s.tabPanel}>
              {loadedTabs.has(tab.key) && renderTab(tab.key)}
            </div>
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}
